/**
 * DataShowcase — antd 데이터 표현(data display) 카탈로그 페이지.
 * (상세 JSDoc 헤더는 본 파일 하단 export default 함수 위 참조)
 */
import {
  Card,
  Tag,
  Descriptions,
  List,
  Statistic,
  Row,
  Col,
  Tree,
  Badge,
  Empty,
  Tour,
  Button,
  Space,
  Typography,
  Timeline
} from 'antd'
import type { TourProps } from 'antd'
import { useRef, useState } from 'react'
import { ClockCircleOutlined } from '@ant-design/icons'
import PageContainer from '@/components/common/PageContainer'
import DataTable from '@/components/common/DataTable'
import { useLabel } from '@/framework/useLabel'

const { Title } = Typography

// ─── 데모 데이터 — Table/Tag 셀 시연용 고정 3행 ─
// 1) key: antd rowKey (string) — 컬럼명 충돌 방지 위한 표준 식별자
// 2) tags: 배열 → render 콜백에서 <Tag/> 매핑
// Why 모듈 상수: 컴포넌트 외부 정의 시 mount 마다 재생성 회피 (참조 동등성 유지)
// Why hardcoded: 실 데이터 연동은 useDataSet/Pattern B — 여기는 시각화만 시연
const dataSource = [
  { key: '1', name: 'Alice', age: 32, tags: ['nice', 'dev'] },
  { key: '2', name: 'Bob', age: 28, tags: ['cool'] },
  { key: '3', name: 'Carol', age: 45, tags: ['senior', 'lead'] }
]

// ─── 트리 데이터 — Root → Child(2) → Leaf 3-level 시연 ─
// defaultExpandAll + showLine 옵션으로 라인 가이드 노출
// 운영 적용: 부서 트리(cm_department), 카테고리(tb_product_category), 메뉴(cm_menu)
// Pitfall: key 가 형제간 UNIQUE 여야 함 — 부모 key 와 dot-notation 권장
const treeData = [
  {
    title: 'Root',
    key: '0',
    children: [
      { title: 'Child 1', key: '0-0' },
      { title: 'Child 2', key: '0-1', children: [{ title: 'Leaf', key: '0-1-0' }] }
    ]
  }
]

// ─── 카드 body 공통 스타일 ─
// height: calc(100% - 38px) — Card head(38px) 제외한 가용 영역
// overflow: auto — 셀 내용 넘침 시 카드 단위 스크롤 (그리드 레이아웃 유지)
// Why `as const`: TS 가 'auto'|'hidden' 등 union 으로 추론하지 않고 리터럴로 좁힘
const cardBodyStyle = { padding: 10, height: 'calc(100% - 38px)', overflow: 'auto' as const }

/**
 * ============================================================================
 * DataShowcase — antd 데이터 표현(data display) 카탈로그
 * ============================================================================
 *
 * 【역할】 KPI 카드 → 표/설명/리스트/트리/타임라인/투어 등 데이터 표현 계열을
 *         하나의 화면에 그리드로 나열.
 *
 * 【시연 항목】
 *   - Statistic       — KPI 4개 (Users/Revenue/Pending/Active)
 *   - DataTable(wrap) — Table + Tag 색상 셀
 *   - Descriptions    — column=1, bordered, Badge 셀
 *   - List            — bordered + actions(수정/삭제)
 *   - Tree            — defaultExpandAll, showLine
 *   - Timeline + Empty + Tour (3-step 가이드)
 *
 * 【사용처】 router/index.tsx — `{ path: '/showcase/data' }` (lazy)
 * 【포팅 원본】 자체 작성 (Wave 16 컴포넌트 카탈로그)
 * 【의존성】 antd Table/Tag/Descriptions/List/Statistic/Tree/Badge/Empty/Tour/Timeline,
 *           project DataTable wrapper, useLabel(t)
 * 【참고】 https://ant.design/components/table
 */
export default function DataShowcase() {
  const { t } = useLabel()
  // ─── 로컬 상태 ─
  // tourOpen: Tour 가이드 모달 표시 여부 — Button onClick → setTourOpen(true) 시작
  // Why useState (not useRef): 토글 시 Tour 컴포넌트 re-render 필요 (open prop 반영)
  const [tourOpen, setTourOpen] = useState(false)
  // ─── DOM ref — Tour target 으로 전달 (스크롤/하이라이트 대상) ─
  // Why useRef: DOM 직접 참조 — Tour 의 target 콜백이 HTMLElement 요구
  // Why 3개 분리: 각 step 의 강조 박스가 독립적 영역을 가리켜야 함
  const statRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLDivElement>(null)
  const treeRef = useRef<HTMLDivElement>(null)

  // ─── Tour 3-step 정의 ─
  // 1) Statistic 영역 → 2) Table 영역 → 3) Tree 영역 순회
  // target 은 lazy 콜백 — 마운트 후 ref.current 안전 참조
  const tourSteps: TourProps['steps'] = [
    {
      title: 'Statistic',
      description: t('LBL_TOUR_STATISTIC_DESC', '핵심 지표를 이 영역에 표시합니다.'),
      target: () => statRef.current as HTMLElement
    },
    {
      title: 'Table',
      description: t('LBL_TOUR_TABLE_DESC', '정렬/필터 지원 기본 테이블입니다.'),
      target: () => tableRef.current as HTMLElement
    },
    {
      title: 'Tree',
      description: t('LBL_TOUR_TREE_DESC', '계층 데이터는 Tree 로 표시합니다.'),
      target: () => treeRef.current as HTMLElement
    }
  ]

  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <div style={{ flex: '0 0 auto', marginBottom: 8 }}>
        <Title level={4} style={{ margin: 0 }}>
          {t('LBL_DATA_COMPONENTS', 'Data 표시 컴포넌트')}
        </Title>
      </div>

      {/* ─── KPI Statistic 4종 ─
          1) Users (정수) / 2) Revenue (소수+suffix='%') /
          3) Pending (Badge prefix) / 4) Active (정수)
          Card size="small" + body padding 8 → 좁은 헤더 영역에 4-up */}
      <div ref={statRef} style={{ flex: '0 0 auto', marginBottom: 8 }}>
        <Row gutter={8}>
          <Col span={6}>
            <Card size="small" styles={{ body: { padding: 8 } }}>
              <Statistic title="Users" value={1128} />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" styles={{ body: { padding: 8 } }}>
              <Statistic title="Revenue" value={93.2} precision={2} suffix="%" />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" styles={{ body: { padding: 8 } }}>
              <Statistic title="Pending" value={3} prefix={<Badge status="processing" />} />
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" styles={{ body: { padding: 8 } }}>
              <Statistic title="Active" value={854} />
            </Card>
          </Col>
        </Row>
      </div>

      {/* ─── Grid: 3 col × 2 row 카탈로그 ─
          row1: Table(span 2) + Descriptions
          row2: List + Tree + Timeline/Empty/Tour
          minHeight: 0 — flex 자식 overflow:auto 정상 동작에 필수 */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gridTemplateRows: '1fr 1fr',
          gap: 10,
          overflow: 'hidden'
        }}
      >
        {/* ─── [Cell 1] Table — DataTable wrapper + Tag 색상 셀 ─ */}
        <div ref={tableRef} style={{ gridColumn: 'span 2', minHeight: 0 }}>
          <Card
            size="small"
            title="Table"
            style={{ height: '100%' }}
            styles={{ body: cardBodyStyle }}
          >
            <DataTable
              autoHeight={false}
              dataSource={dataSource}
              columns={[
                { title: 'Name', dataIndex: 'name' },
                { title: 'Age', dataIndex: 'age', width: 80 },
                {
                  title: 'Tags',
                  dataIndex: 'tags',
                  render: (tags: string[]) => tags.map((t) => <Tag key={t}>{t}</Tag>)
                }
              ]}
              pagination={false}
              size="small"
            />
          </Card>
        </div>

        {/* ─── [Cell 2] Descriptions — 1열 bordered + Badge 셀 ─
            Name/Role/Email/Status 4개 항목, Status 는 Badge status="success" */}
        <Card
          size="small"
          title="Descriptions"
          style={{ height: '100%' }}
          styles={{ body: cardBodyStyle }}
        >
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Name">Alice</Descriptions.Item>
            <Descriptions.Item label="Role">Engineer</Descriptions.Item>
            <Descriptions.Item label="Email">alice@example.com</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge status="success" text="Active" />
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* ─── [Cell 3] List — bordered + actions(수정/삭제) ─
            renderItem 콜백에서 각 항목에 액션 링크 부여 */}
        <Card size="small" title="List" style={{ height: '100%' }} styles={{ body: cardBodyStyle }}>
          <List
            size="small"
            bordered
            dataSource={[
              t('LBL_DEMO_TASK_API', 'API 설계'),
              t('LBL_DEMO_TASK_DB', 'DB 마이그레이션'),
              t('LBL_DEMO_TASK_FE', '프론트 레이아웃'),
              t('LBL_DEMO_TASK_TEST', '테스트 작성')
            ]}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <a key="edit">{t('BTN_EDIT', '수정')}</a>,
                  <a key="del">{t('BTN_DELETE', '삭제')}</a>
                ]}
              >
                {item}
              </List.Item>
            )}
          />
        </Card>

        {/* ─── [Cell 4] Tree — defaultExpandAll + showLine ─
            계층 데이터(부서/카테고리/메뉴) 시각화 표준 */}
        <div ref={treeRef} style={{ minHeight: 0 }}>
          <Card
            size="small"
            title="Tree"
            style={{ height: '100%' }}
            styles={{ body: cardBodyStyle }}
          >
            <Tree treeData={treeData} defaultExpandAll showLine />
          </Card>
        </div>

        {/* ─── [Cell 5] Timeline + Empty + Tour 트리거 ─
            1) Timeline: 4-step (생성→완료→QA→배포예정)
            2) Tour 시작 버튼 → setTourOpen(true) → tourSteps 순회
            3) Empty: 데이터 부재 표현 SIMPLE 변형 */}
        <Card
          size="small"
          title="Timeline / Empty / Tour"
          style={{ height: '100%' }}
          styles={{ body: cardBodyStyle }}
        >
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Timeline
              items={[
                { children: `${t('LBL_TL_PROJECT_CREATE', '프로젝트 생성')} 2024-01-01` },
                { children: `${t('LBL_TL_DEV_INIT_DONE', '초기 개발 완료')} 2024-02-15` },
                {
                  color: 'blue',
                  dot: <ClockCircleOutlined />,
                  children: t('LBL_TL_QA_IN_PROGRESS', 'QA 진행 중')
                },
                { color: 'gray', children: t('LBL_TL_PROD_DEPLOY_PLAN', '프로덕션 배포 예정') }
              ]}
            />
            <Space>
              <Button size="small" type="primary" onClick={() => setTourOpen(true)}>
                {t('BTN_START_TOUR', '투어 시작')}
              </Button>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Empty"
                style={{ margin: 0 }}
              />
            </Space>
            <Tour open={tourOpen} onClose={() => setTourOpen(false)} steps={tourSteps} />
          </Space>
        </Card>
      </div>
    </PageContainer>
  )
}
